import React, { useEffect, useState } from "react";
import { Download, Film } from "lucide-react";
import MovToMp4Converter from "../MovToMp4Converter";
import { getActiveCustomerId } from "./imageCreationService";
import { ACTIVE_CUSTOMER_ID_CHANGED_EVENT } from "../../hooks/useMerchantStores";

// Workflow card: convert a MOV to an H.264 + AAC MP4 (Roku-safe), preview it, and download it.

export default function MovToMp4Card({ onRemove }) {
  const [customerId, setCustomerId] = useState(getActiveCustomerId());
  const [mp4, setMp4] = useState(null); // { file, url, converted }

  useEffect(() => {
    const sync = () => setCustomerId(getActiveCustomerId());
    window.addEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, sync);
    return () => window.removeEventListener(ACTIVE_CUSTOMER_ID_CHANGED_EVENT, sync);
  }, []);

  // Free the preview URL when it is replaced or the card unmounts.
  useEffect(() => () => mp4 && URL.revokeObjectURL(mp4.url), [mp4]);

  return (
    <article className="rounded-2xl border border-gray-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 sm:px-5">
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-indigo-50 text-indigo-600">
            <Film size={18} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 sm:text-base">MOV to MP4</h3>
            <p className="text-xs text-gray-500">Convert a MOV video to H.264 video + AAC audio MP4 (plays on Roku)</p>
          </div>
        </div>
        {onRemove && (
          <button type="button" onClick={onRemove} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-gray-400">
            Remove from Components
          </button>
        )}
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="border-b border-gray-200 p-4 sm:p-5 lg:border-b-0 lg:border-r">
          <MovToMp4Converter
            customerId={customerId}
            showHeader={false}
            className="border-0 p-0"
            onConverted={(file, info) => setMp4({ file, url: URL.createObjectURL(file), converted: info.converted })}
            onError={() => setMp4(null)}
          />
        </div>
        <div className="flex flex-col gap-3 p-4 sm:p-5">
          {mp4 ? (
            <>
              <video src={mp4.url} controls className="aspect-video w-full rounded-lg bg-black" />
              <a
                href={mp4.url}
                download={mp4.file.name}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700"
              >
                <Download size={16} /> Download {mp4.file.name}
              </a>
            </>
          ) : (
            <div className="grid min-h-[160px] place-items-center rounded-lg border border-dashed border-gray-200 text-sm text-gray-400">
              The converted video will appear here
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
